import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| API INSTANCE
|--------------------------------------------------------------------------
*/

const api =
    new API();

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getResponseBody = (
    response
) => {
    return (
        response?.data ||
        response ||
        {}
    );
};

const getResponseData = (
    response
) => {
    const body =
        getResponseBody(
            response
        );

    return (
        body?.data ??
        body
    );
};

const getErrorMessage = (
    error,
    fallbackMessage
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.response?.data
            ?.error ||
        error?.payload?.message ||
        error?.message ||
        (
            typeof error ===
                "string"
                ? error
                : ""
        ) ||
        fallbackMessage
    );
};

const getDebitAmount = (
    debitNote
) => {
    const value =
        Number(
            debitNote
                ?.totals
                ?.debitAmount ??
            debitNote
                ?.debitAmount ??
            debitNote
                ?.totalAmount ??
            0
        );

    return Number.isFinite(
        value
    )
        ? value
        : 0;
};

const updateRecordById = (
    records,
    updatedRecord
) => {
    if (
        !Array.isArray(
            records
        )
    ) {
        return [];
    }

    if (
        !updatedRecord?._id
    ) {
        return records;
    }

    return records.map(
        (
            record
        ) =>
            String(
                record?._id ||
                ""
            ) ===
                String(
                    updatedRecord._id
                )
                ? updatedRecord
                : record
    );
};

const addRecordIfMissing = (
    records,
    newRecord
) => {
    const normalizedRecords =
        Array.isArray(
            records
        )
            ? records
            : [];

    if (
        !newRecord?._id
    ) {
        return normalizedRecords;
    }

    const exists =
        normalizedRecords.some(
            (
                record
            ) =>
                String(
                    record?._id ||
                        ""
                ) ===
                String(
                    newRecord._id
                )
        );

    if (
        exists
    ) {
        return updateRecordById(
            normalizedRecords,
            newRecord
        );
    }

    return [
        newRecord,
        ...normalizedRecords,
    ];
};

const buildSummaryFromRecords = (
    records = []
) => {
    const normalizedRecords =
        Array.isArray(
            records
        )
            ? records
            : [];

    return normalizedRecords.reduce(
        (
            summary,
            debitNote
        ) => {
            const status =
                String(
                    debitNote
                        ?.status ||
                        "DRAFT"
                ).toUpperCase();

            summary.total +=
                1;

            if (
                status ===
                "DRAFT"
            ) {
                summary.draft +=
                    1;
            }

            if (
                status ===
                "CONFIRMED"
            ) {
                summary.confirmed +=
                    1;

                summary.confirmedDebitAmount +=
                    getDebitAmount(
                        debitNote
                    );
            }

            if (
                status ===
                "CANCELLED"
            ) {
                summary.cancelled +=
                    1;
            }

            summary.totalDebitAmount +=
                getDebitAmount(
                    debitNote
                );

            return summary;
        },
        {
            total: 0,
            draft: 0,
            confirmed: 0,
            cancelled: 0,
            confirmedDebitAmount: 0,
            totalDebitAmount: 0,
        }
    );
};

/*
|--------------------------------------------------------------------------
| CREATE DEBIT NOTE
|--------------------------------------------------------------------------
*/

export const createDebitNote =
    createAsyncThunk(
        "debitNote/createDebitNote",
        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            try {
                const response =
                    await api.post(
                        config.createDebitNote(),
                        payload
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to create debit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH ALL DEBIT NOTES
|--------------------------------------------------------------------------
*/

export const fetchDebitNotes =
    createAsyncThunk(
        "debitNote/fetchDebitNotes",
        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const query =
                    new URLSearchParams();

                Object.entries(
                    params ||
                        {}
                ).forEach(
                    ([
                        key,
                        value,
                    ]) => {
                        if (
                            value !==
                                undefined &&
                            value !==
                                null &&
                            value !==
                                ""
                        ) {
                            query.append(
                                key,
                                String(
                                    value
                                )
                            );
                        }
                    }
                );

                const baseUrl =
                    config.getDebitNotes();

                const url =
                    query.toString()
                        ? `${baseUrl}?${query.toString()}`
                        : baseUrl;

                const response =
                    await api.get(
                        url
                    );

                const body =
                    getResponseBody(
                        response
                    );

                const data =
                    body?.data ??
                    body ??
                    {};

                const records =
                    Array.isArray(
                        data
                    )
                        ? data
                        : Array.isArray(
                            data?.records
                        )
                            ? data.records
                            : Array.isArray(
                                data?.debitNotes
                            )
                                ? data.debitNotes
                                : [];

                const summary =
                    data?.summary ||
                    body?.summary ||
                    {};

                const pagination =
                    body?.pagination ||
                    data?.pagination ||
                    {};

                return {
                    records,

                    summary,

                    pagination: {
                        page:
                            Number(
                                pagination
                                    ?.page ||
                                params
                                    ?.page ||
                                1
                            ),

                        limit:
                            Number(
                                pagination
                                    ?.limit ||
                                params
                                    ?.limit ||
                                20
                            ),

                        total:
                            Number(
                                pagination
                                    ?.total ??
                                records.length
                            ),

                        totalPages:
                            Number(
                                pagination
                                    ?.totalPages ||
                                1
                            ),

                        hasNextPage:
                            Boolean(
                                pagination
                                    ?.hasNextPage
                            ),

                        hasPreviousPage:
                            Boolean(
                                pagination
                                    ?.hasPreviousPage
                            ),
                    },
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch debit notes."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH DEBIT NOTE BY ID
|--------------------------------------------------------------------------
*/

export const fetchDebitNoteById =
    createAsyncThunk(
        "debitNote/fetchDebitNoteById",
        async (
            debitNoteId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !debitNoteId
                ) {
                    return rejectWithValue(
                        "Debit note ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getDebitNoteById(
                            debitNoteId
                        )
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch debit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH DEBIT NOTES BY PURCHASE BILL
|--------------------------------------------------------------------------
*/

export const fetchDebitNotesByPurchaseBill =
    createAsyncThunk(
        "debitNote/fetchDebitNotesByPurchaseBill",
        async (
            {
                purchaseBillId,
                includeCancelled = false,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !purchaseBillId
                ) {
                    return rejectWithValue(
                        "Purchase bill ID is required."
                    );
                }

                const baseUrl =
                    config.getDebitNotesByPurchaseBill(
                        purchaseBillId
                    );

                const query =
                    new URLSearchParams();

                query.append(
                    "includeCancelled",
                    String(
                        includeCancelled
                    )
                );

                const response =
                    await api.get(
                        `${baseUrl}?${query.toString()}`
                    );

                const body =
                    getResponseBody(
                        response
                    );

                const data =
                    body?.data ||
                    body ||
                    {};

                return {
                    records:
                        Array.isArray(
                            data?.records
                        )
                            ? data.records
                            : Array.isArray(
                                data
                            )
                                ? data
                                : [],

                    summary: {
                        total:
                            Number(
                                data?.summary
                                    ?.total ||
                                0
                            ),

                        confirmed:
                            Number(
                                data?.summary
                                    ?.confirmed ||
                                0
                            ),

                        draft:
                            Number(
                                data?.summary
                                    ?.draft ||
                                0
                            ),

                        cancelled:
                            Number(
                                data?.summary
                                    ?.cancelled ||
                                0
                            ),

                        confirmedDebitAmount:
                            Number(
                                data?.summary
                                    ?.confirmedDebitAmount ||
                                0
                            ),
                    },
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch purchase bill debit notes."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CONFIRM DEBIT NOTE
|--------------------------------------------------------------------------
*/

export const confirmDebitNote =
    createAsyncThunk(
        "debitNote/confirmDebitNote",
        async (
            debitNoteId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !debitNoteId
                ) {
                    return rejectWithValue(
                        "Debit note ID is required."
                    );
                }

                const response =
                    await api.patch(
                        config.confirmDebitNote(
                            debitNoteId
                        ),
                        {}
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to confirm debit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CANCEL DEBIT NOTE
|--------------------------------------------------------------------------
*/

export const cancelDebitNote =
    createAsyncThunk(
        "debitNote/cancelDebitNote",
        async (
            {
                debitNoteId,
                cancellationReason,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !debitNoteId
                ) {
                    return rejectWithValue(
                        "Debit note ID is required."
                    );
                }

                const normalizedReason =
                    String(
                        cancellationReason ||
                            ""
                    ).trim();

                if (
                    normalizedReason.length <
                    3
                ) {
                    return rejectWithValue(
                        "Cancellation reason must contain at least 3 characters."
                    );
                }

                const response =
                    await api.patch(
                        config.cancelDebitNote(
                            debitNoteId
                        ),
                        {
                            cancellationReason:
                                normalizedReason,
                        }
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to cancel debit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const defaultPurchaseBillSummary = {
    total: 0,
    confirmed: 0,
    draft: 0,
    cancelled: 0,
    confirmedDebitAmount: 0,
};

const defaultListSummary = {
    total: 0,
    draft: 0,
    confirmed: 0,
    cancelled: 0,
    confirmedDebitAmount: 0,
    totalDebitAmount: 0,
};

const defaultPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};

const defaultLoading = {
    list: false,
    details: false,
    purchaseBill: false,
    create: false,
    confirm: false,
    cancel: false,
};

const defaultError = {
    list: "",
    details: "",
    purchaseBill: "",
    create: "",
    confirm: "",
    cancel: "",
};

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    records: [],

    listSummary: {
        ...defaultListSummary,
    },

    pagination: {
        ...defaultPagination,
    },

    selectedDebitNote:
        null,

    purchaseBillDebitNotes:
        [],

    purchaseBillSummary: {
        ...defaultPurchaseBillSummary,
    },

    loading: {
        ...defaultLoading,
    },

    error: {
        ...defaultError,
    },

    successMessage:
        "",
};

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const debitNoteSlice =
    createSlice({
        name:
            "debitNote",

        initialState,

        reducers: {
            clearDebitNoteError: (
                state
            ) => {
                state.error = {
                    ...defaultError,
                };
            },

            clearDebitNoteSuccess: (
                state
            ) => {
                state.successMessage =
                    "";
            },

            clearSelectedDebitNote: (
                state
            ) => {
                state.selectedDebitNote =
                    null;

                state.error.details =
                    "";
            },

            clearPurchaseBillDebitNotes: (
                state
            ) => {
                state.purchaseBillDebitNotes =
                    [];

                state.purchaseBillSummary = {
                    ...defaultPurchaseBillSummary,
                };

                state.error.purchaseBill =
                    "";
            },

            resetDebitNoteState: (
                state
            ) => {
                state.records =
                    [];

                state.listSummary = {
                    ...defaultListSummary,
                };

                state.pagination = {
                    ...defaultPagination,
                };

                state.selectedDebitNote =
                    null;

                state.purchaseBillDebitNotes =
                    [];

                state.purchaseBillSummary = {
                    ...defaultPurchaseBillSummary,
                };

                state.loading = {
                    ...defaultLoading,
                };

                state.error = {
                    ...defaultError,
                };

                state.successMessage =
                    "";
            },
        },

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | CREATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createDebitNote.pending,
                    (
                        state
                    ) => {
                        state.loading.create =
                            true;

                        state.error.create =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    createDebitNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        const created =
                            action.payload;

                        state.selectedDebitNote =
                            created;

                        state.records =
                            addRecordIfMissing(
                                state.records,
                                created
                            );

                        state.purchaseBillDebitNotes =
                            addRecordIfMissing(
                                state.purchaseBillDebitNotes,
                                created
                            );

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.records
                            );

                        state.purchaseBillSummary = {
                            ...state.purchaseBillSummary,

                            ...buildSummaryFromRecords(
                                state.purchaseBillDebitNotes
                            ),
                        };

                        state.successMessage =
                            "Debit note created successfully.";
                    }
                )

                .addCase(
                    createDebitNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.error.create =
                            action.payload ||
                            "Unable to create debit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchDebitNotes.pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            "";
                    }
                )

                .addCase(
                    fetchDebitNotes.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.records =
                            Array.isArray(
                                action.payload
                                    ?.records
                            )
                                ? action.payload.records
                                : [];

                        const localSummary =
                            buildSummaryFromRecords(
                                state.records
                            );

                        state.listSummary = {
                            ...defaultListSummary,

                            ...localSummary,

                            ...(
                                action.payload
                                    ?.summary ||
                                {}
                            ),
                        };

                        state.pagination = {
                            ...defaultPagination,

                            ...(
                                action.payload
                                    ?.pagination ||
                                {}
                            ),
                        };
                    }
                )

                .addCase(
                    fetchDebitNotes.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            "Unable to fetch debit notes.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchDebitNoteById.pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            "";
                    }
                )

                .addCase(
                    fetchDebitNoteById.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.selectedDebitNote =
                            action.payload;
                    }
                )

                .addCase(
                    fetchDebitNoteById.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            "Unable to fetch debit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | PURCHASE BILL DEBIT NOTES
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchDebitNotesByPurchaseBill.pending,
                    (
                        state
                    ) => {
                        state.loading.purchaseBill =
                            true;

                        state.error.purchaseBill =
                            "";
                    }
                )

                .addCase(
                    fetchDebitNotesByPurchaseBill.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.purchaseBill =
                            false;

                        state.purchaseBillDebitNotes =
                            action.payload
                                ?.records ||
                            [];

                        const localSummary =
                            buildSummaryFromRecords(
                                state.purchaseBillDebitNotes
                            );

                        state.purchaseBillSummary = {
                            ...defaultPurchaseBillSummary,

                            ...localSummary,

                            ...(
                                action.payload
                                    ?.summary ||
                                {}
                            ),
                        };
                    }
                )

                .addCase(
                    fetchDebitNotesByPurchaseBill.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.purchaseBill =
                            false;

                        state.error.purchaseBill =
                            action.payload ||
                            "Unable to fetch purchase bill debit notes.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CONFIRM
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    confirmDebitNote.pending,
                    (
                        state
                    ) => {
                        state.loading.confirm =
                            true;

                        state.error.confirm =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    confirmDebitNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        const confirmed =
                            action.payload;

                        state.selectedDebitNote =
                            confirmed;

                        state.records =
                            updateRecordById(
                                state.records,
                                confirmed
                            );

                        state.purchaseBillDebitNotes =
                            updateRecordById(
                                state.purchaseBillDebitNotes,
                                confirmed
                            );

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.records
                            );

                        state.purchaseBillSummary = {
                            ...state.purchaseBillSummary,

                            ...buildSummaryFromRecords(
                                state.purchaseBillDebitNotes
                            ),
                        };

                        state.successMessage =
                            "Debit note confirmed successfully.";
                    }
                )

                .addCase(
                    confirmDebitNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        state.error.confirm =
                            action.payload ||
                            "Unable to confirm debit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CANCEL
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    cancelDebitNote.pending,
                    (
                        state
                    ) => {
                        state.loading.cancel =
                            true;

                        state.error.cancel =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    cancelDebitNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        const cancelled =
                            action.payload;

                        state.selectedDebitNote =
                            cancelled;

                        state.records =
                            updateRecordById(
                                state.records,
                                cancelled
                            );

                        state.purchaseBillDebitNotes =
                            updateRecordById(
                                state.purchaseBillDebitNotes,
                                cancelled
                            );

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.records
                            );

                        state.purchaseBillSummary = {
                            ...state.purchaseBillSummary,

                            ...buildSummaryFromRecords(
                                state.purchaseBillDebitNotes
                            ),
                        };

                        state.successMessage =
                            "Debit note cancelled successfully.";
                    }
                )

                .addCase(
                    cancelDebitNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        state.error.cancel =
                            action.payload ||
                            "Unable to cancel debit note.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearDebitNoteError,
    clearDebitNoteSuccess,
    clearSelectedDebitNote,
    clearPurchaseBillDebitNotes,
    resetDebitNoteState,
} = debitNoteSlice.actions;

/*
|--------------------------------------------------------------------------
| ROOT SELECTOR
|--------------------------------------------------------------------------
|
| Register reducer as:
|
| debitNote: debitNoteReducer
|--------------------------------------------------------------------------
*/

export const debitNoteStateSelector = (
    state
) =>
    state?.debitNote ||
    initialState;

/*
|--------------------------------------------------------------------------
| LIST SELECTORS
|--------------------------------------------------------------------------
*/

export const debitNotesSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).records ||
    [];

export const debitNoteListSummarySelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).listSummary ||
    defaultListSummary;

export const debitNotePaginationSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).pagination ||
    defaultPagination;

/*
|--------------------------------------------------------------------------
| DETAILS
|--------------------------------------------------------------------------
*/

export const selectedDebitNoteSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).selectedDebitNote ||
    null;

/*
|--------------------------------------------------------------------------
| PURCHASE BILL
|--------------------------------------------------------------------------
*/

export const purchaseBillDebitNotesSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).purchaseBillDebitNotes ||
    [];

export const purchaseBillDebitNoteSummarySelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).purchaseBillSummary ||
    defaultPurchaseBillSummary;

/*
|--------------------------------------------------------------------------
| LOADING / ERROR / SUCCESS
|--------------------------------------------------------------------------
*/

export const debitNoteLoadingSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).loading ||
    defaultLoading;

export const debitNoteErrorSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).error ||
    defaultError;

export const debitNoteSuccessSelector = (
    state
) =>
    debitNoteStateSelector(
        state
    ).successMessage ||
    "";

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default debitNoteSlice.reducer;